import { DocumentDto, getBase64FromBaseString, Document } from '@ecdlink/core';
import { DocumentInput } from '@ecdlink/graphql';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { DocumentService } from '@services/DocumentService';
import { UserService } from '@services/UserService';
import { RootState, ThunkApiType } from '../types';

export const DocumentsActions = {
  GET_DOCUMENTS: 'getDocuments',
  CREATE_DOCUMENT: 'createDocument',
  DELETE_DOCUMENT: 'deleteDocument',
  CREATE_FILE: 'createFile',
  UPSERT_DOCUMENTS: 'upsertDocuments',
};

export const getDocuments = createAsyncThunk<
  DocumentDto[],
  // eslint-disable-next-line @typescript-eslint/ban-types
  {},
  ThunkApiType<RootState>
>(
  DocumentsActions.GET_DOCUMENTS,
  // eslint-disable-next-line no-empty-pattern
  async ({}, { getState, rejectWithValue }) => {
    const {
      auth: { userAuth },
      documents: { documents: documentCache },
    } = getState();

    if (!documentCache) {
      try {
        let documents: DocumentDto[] | undefined;

        if (userAuth?.auth_token) {
          documents = await new DocumentService(
            userAuth?.auth_token
          ).getdocuments(userAuth.id);
        } else {
          return rejectWithValue('no access token, profile check required');
        }

        if (!documents) {
          return rejectWithValue('Error getting Documents');
        }

        return documents;
      } catch (err) {
        return rejectWithValue(err);
      }
    } else {
      return documentCache;
    }
  }
);

export const getDocumentsForCoach = createAsyncThunk<
  DocumentDto[],
  // eslint-disable-next-line @typescript-eslint/ban-types
  {},
  ThunkApiType<RootState>
>(
  DocumentsActions.GET_DOCUMENTS,
  // eslint-disable-next-line no-empty-pattern
  async ({}, { getState, rejectWithValue }) => {
    const {
      auth: { userAuth },
      documents: { documents: documentCache },
    } = getState();

    if (!documentCache) {
      try {
        let documents: DocumentDto[] | undefined;

        if (userAuth?.auth_token) {
          documents = await new DocumentService(
            userAuth?.auth_token
          ).getAllChildrenDocumentsForCoachV2();
        } else {
          return rejectWithValue('no access token, profile check required');
        }

        if (!documents) {
          return rejectWithValue('Error getting Documents');
        }

        return documents;
      } catch (err) {
        return rejectWithValue(err);
      }
    } else {
      return documentCache;
    }
  }
);

export const deleteDocument = createAsyncThunk<
  boolean,
  { fileId: string; fileName: string },
  ThunkApiType<RootState>
>(
  DocumentsActions.DELETE_DOCUMENT,
  // eslint-disable-next-line no-empty-pattern
  async ({ fileId, fileName }, { getState, rejectWithValue }) => {
    const {
      auth: { userAuth },
      documents: { documents },
    } = getState();
    if (userAuth?.auth_token && documents) {
      try {
        const _documentService = new DocumentService(userAuth?.auth_token);
        return await _documentService.deleteDocument(fileId, fileName);
      } catch (err) {
        return rejectWithValue(err);
      }
    }
    return false; // Return an empty array if conditions are not met
  }
);

export const createFile = createAsyncThunk<
  Awaited<ReturnType<DocumentService['fileUpload']>>, // Explicitly handle the promise return type
  { base64File: string; fileName: string; fileType: string }, // Input payload type
  ThunkApiType<RootState> // Thunk API type
>(
  DocumentsActions.CREATE_FILE,
  async ({ base64File, fileName, fileType }, { getState, rejectWithValue }) => {
    const {
      auth: { userAuth },
    } = getState();

    if (userAuth?.auth_token) {
      try {
        const splitString = getBase64FromBaseString(base64File);
        const _documentService = new DocumentService(userAuth.auth_token);
        const result = await _documentService.fileUpload(
          splitString,
          fileName,
          fileType
        );
        return result;
      } catch (err: any) {
        return rejectWithValue({
          message: err.message || 'Unknown error',
          code: err.code || 'UNKNOWN_ERROR',
        });
      }
    }

    return rejectWithValue({ message: 'Authentication token is missing.' });
  }
);

export const createDocument = createAsyncThunk<
  boolean,
  // eslint-disable-next-line @typescript-eslint/ban-types
  { document: Document },
  ThunkApiType<RootState>
>(
  DocumentsActions.CREATE_DOCUMENT,
  // eslint-disable-next-line no-empty-pattern
  async ({ document }, { getState, rejectWithValue }) => {
    const {
      auth: { userAuth },
    } = getState();

    try {
      if (userAuth?.auth_token && document) {
        if (document.file) {
          const splitString = getBase64FromBaseString(document.file);

          const _documentService = new DocumentService(userAuth?.auth_token);
          const _userService = new UserService(userAuth?.auth_token);

          const existingUser = await _userService.doesUserExist(
            document.userId ?? ''
          );

          if (!existingUser) {
            return false;
          }

          const fileReturnModel = await _documentService.fileUpload(
            splitString,
            document.name,
            document.fileType ?? ''
          );

          if (fileReturnModel) {
            const documentInputModel: DocumentInput = {
              Id: document.id,
              UserId: document.userId,
              WorkflowStatusId: document.workflowStatusId,
              DocumentTypeId: document.documentTypeId,
              Name: fileReturnModel.name,
              Reference: fileReturnModel.url,
              CreatedUserId: document.createdUserId,
              DocumentDate: document.documentDate,
              IsActive: true,
            };

            await _documentService.updateDocument(
              document.id ?? '',
              documentInputModel
            );
          }
        }
      }
      return true;
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);

export const upsertDocuments = createAsyncThunk<
  boolean[],
  // eslint-disable-next-line @typescript-eslint/ban-types
  {},
  ThunkApiType<RootState>
>(
  DocumentsActions.UPSERT_DOCUMENTS,
  // eslint-disable-next-line no-empty-pattern
  async ({}, { getState, rejectWithValue }) => {
    const {
      auth: { userAuth },
      documents: { documents },
    } = getState();

    try {
      if (userAuth?.auth_token && documents) {
        for (const document of documents) {
          if (document.file) {
            const splitString = getBase64FromBaseString(document.file);

            const _documentService = new DocumentService(userAuth?.auth_token);
            const _userService = new UserService(userAuth?.auth_token);

            const existingUser = await _userService.doesUserExist(
              document.userId ?? ''
            );

            if (!existingUser) {
              continue;
            }

            const fileReturnModel = await _documentService.fileUpload(
              splitString,
              document.name,
              document.fileType ?? ''
            );

            if (fileReturnModel) {
              const documentInputModel: DocumentInput = {
                Id: document.id,
                UserId: document.userId,
                WorkflowStatusId: document.workflowStatusId,
                DocumentTypeId: document.documentTypeId,
                Name: fileReturnModel.name,
                Reference: fileReturnModel.url,
                CreatedUserId: document.createdUserId,
                DocumentDate: document.documentDate,
                IsActive: true,
              };

              await _documentService.updateDocument(
                document.id ?? '',
                documentInputModel
              );
            }
          }
        }
      }
      return [true];
    } catch (err) {
      return rejectWithValue(err);
    }
  }
);
